import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Shield,
  FileText,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  Download,
  Trash2,
  Eye,
  Globe,
  Lock,
  Mail,
  Search,
  Filter
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminCompliance() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [dsars, setDsars] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [reportRes, dsarRes, auditRes, subRes] = await Promise.all([
        fetch(`${API_URL}/api/privacy/admin/compliance-report`, { headers }),
        fetch(`${API_URL}/api/privacy/admin/dsars`, { headers }),
        fetch(`${API_URL}/api/privacy/admin/audit-log?limit=50`, { headers }),
        fetch(`${API_URL}/api/privacy/marketing/subscribers?limit=100`, { headers })
      ]);

      if (reportRes.ok) setReport(await reportRes.json());
      if (dsarRes.ok) {
        const data = await dsarRes.json();
        setDsars(data.dsars || []);
      }
      if (auditRes.ok) {
        const data = await auditRes.json();
        setAuditLogs(data.logs || []);
      }
      if (subRes.ok) {
        const data = await subRes.json();
        setSubscribers(data.subscribers || []);
      }
    } catch (error) {
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const updateDsarStatus = async (dsarId, status) => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/privacy/admin/dsar/${dsarId}?status=${status}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`DSAR marked as ${status}`);
        loadAllData();
      } else {
        toast.error("Failed to update DSAR");
      }
    } catch (error) {
      toast.error("Failed to update DSAR");
    }
  };

  const runDataRetention = async () => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/privacy/admin/data-retention-cleanup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Cleanup complete: ${result.recordings_deleted} recordings, ${result.analytics_deleted} analytics events deleted`);
      } else {
        toast.error("Failed to run cleanup");
      }
    } catch (error) {
      toast.error("Failed to run data retention cleanup");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { class: "bg-amber-100 text-amber-800", icon: Clock },
      in_progress: { class: "bg-blue-100 text-blue-800", icon: RefreshCw },
      completed: { class: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
      rejected: { class: "bg-red-100 text-red-800", icon: AlertTriangle }
    };
    const v = variants[status] || variants.pending;
    const Icon = v.icon;
    return (
      <Badge className={`${v.class} gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getRegionBadge = (region) => {
    const regions = {
      ca: { label: "Canada (PIPEDA)", color: "bg-red-100 text-red-800" },
      us: { label: "USA (CCPA)", color: "bg-blue-100 text-blue-800" },
      eu: { label: "EU (GDPR)", color: "bg-indigo-100 text-indigo-800" },
      ae: { label: "UAE (PDPL)", color: "bg-emerald-100 text-emerald-800" },
      global: { label: "Global", color: "bg-slate-100 text-slate-800" }
    };
    const r = regions[region] || regions.global;
    return <Badge className={r.color}>{r.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-compliance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Privacy & Compliance</h1>
          <p className="text-slate-500 mt-1">PIPEDA, GDPR, CCPA, UAE PDPL compliance management</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runDataRetention} variant="outline" data-testid="run-retention-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Run Data Retention
          </Button>
          <Button onClick={loadAllData} variant="outline" data-testid="refresh-compliance">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="dsars-pending-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending DSARs</p>
                <p className="text-3xl font-bold text-slate-900">{report?.dsar?.pending || 0}</p>
                <p className="text-sm text-amber-600 mt-1">
                  {report?.dsar?.overdue || 0} overdue
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-100">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="consent-records-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Consent Records</p>
                <p className="text-3xl font-bold text-slate-900">{report?.consents?.total_records || 0}</p>
                <p className="text-sm text-emerald-600 mt-1">
                  {report?.consents?.marketing_opted_in || 0} marketing opt-in
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-100">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="subscribers-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Marketing Subscribers</p>
                <p className="text-3xl font-bold text-slate-900">{report?.marketing?.active_subscribers || 0}</p>
                <p className="text-sm text-red-600 mt-1">
                  {report?.marketing?.unsubscribed_this_month || 0} unsubscribed (30d)
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-blue-100">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="deletions-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Data Deletions</p>
                <p className="text-3xl font-bold text-slate-900">{report?.data_deletion?.requests_this_month || 0}</p>
                <p className="text-sm text-slate-500 mt-1">This month</p>
              </div>
              <div className="p-2.5 rounded-lg bg-red-100">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="dsars" data-testid="tab-dsars">Data Requests ({dsars.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Regional Compliance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Regional Compliance
                </CardTitle>
                <CardDescription>Data protection regulations by region</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-red-800">CA</span>
                    </div>
                    <div>
                      <p className="font-medium">Canada - PIPEDA</p>
                      <p className="text-sm text-slate-500">Home region - Full compliance</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-800">US</span>
                    </div>
                    <div>
                      <p className="font-medium">USA - CCPA/CPRA</p>
                      <p className="text-sm text-slate-500">California residents</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-indigo-800">EU</span>
                    </div>
                    <div>
                      <p className="font-medium">Europe - GDPR</p>
                      <p className="text-sm text-slate-500">EU/EEA residents</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-emerald-800">AE</span>
                    </div>
                    <div>
                      <p className="font-medium">UAE - PDPL</p>
                      <p className="text-sm text-slate-500">UAE data subjects</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention Policies */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Data Retention Policies
                </CardTitle>
                <CardDescription>Automatic data cleanup schedules</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Call Recordings</p>
                    <p className="text-sm text-slate-500">Audio files from calls</p>
                  </div>
                  <Badge>90 days</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Call Transcripts</p>
                    <p className="text-sm text-slate-500">Text transcriptions</p>
                  </div>
                  <Badge>1 year</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Analytics Data</p>
                    <p className="text-sm text-slate-500">Usage & performance metrics</p>
                  </div>
                  <Badge>2 years</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Audit Logs</p>
                    <p className="text-sm text-slate-500">Compliance audit trail</p>
                  </div>
                  <Badge>7 years</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Deleted User Data</p>
                    <p className="text-sm text-slate-500">Anonymized records</p>
                  </div>
                  <Badge>30 days</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* DSARs Tab */}
        <TabsContent value="dsars" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Data Subject Access Requests</CardTitle>
                  <CardDescription>GDPR Article 15, CCPA, PIPEDA requests</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {dsars.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No data requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dsars.map((dsar) => (
                    <div key={dsar.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50" data-testid={`dsar-${dsar.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{dsar.user_email}</p>
                          {getRegionBadge(dsar.region)}
                        </div>
                        <p className="text-sm text-slate-500">
                          <span className="font-medium capitalize">{dsar.request_type}</span> request
                          {dsar.details && ` - ${dsar.details}`}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Created: {new Date(dsar.created_at).toLocaleDateString()} | 
                          Deadline: {new Date(dsar.deadline).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(dsar.status)}
                        {dsar.status === "pending" && (
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => updateDsarStatus(dsar.id, "in_progress")}>
                              Start
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => updateDsarStatus(dsar.id, "completed")}>
                              Complete
                            </Button>
                          </div>
                        )}
                        {dsar.status === "in_progress" && (
                          <Button size="sm" onClick={() => updateDsarStatus(dsar.id, "completed")}>
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Privacy Audit Log</CardTitle>
              <CardDescription>All privacy-related events for compliance reporting</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Eye className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No audit events yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-center gap-4 p-3 border rounded-lg text-sm" data-testid={`audit-${log.id}`}>
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <div className="flex-1">
                        <p className="font-medium capitalize">{log.event_type.replace(/_/g, " ")}</p>
                        <p className="text-slate-500">User: {log.user_id}</p>
                      </div>
                      <p className="text-slate-400">{new Date(log.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscribers Tab */}
        <TabsContent value="subscribers" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Marketing Subscribers</CardTitle>
                  <CardDescription>Double opt-in confirmed subscribers (CASL compliant)</CardDescription>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search subscribers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                    data-testid="search-subscribers"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {subscribers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No subscribers yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {subscribers
                    .filter(s => s.email.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((sub) => (
                      <div key={sub.email} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`subscriber-${sub.email}`}>
                        <div>
                          <p className="font-medium">{sub.email}</p>
                          <p className="text-sm text-slate-500">
                            Source: {sub.source} | Subscribed: {new Date(sub.subscribed_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={sub.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-800"}>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
